import { useCallback, useState } from 'react';

import ProspectsService from '../../services/api/prospects/prospects';
import { ProspectStatus, type ProspectStatus as ProspectStatusType } from '../../types/prospects';

interface IUseProspectStatusUpdateProps {
  brokerId: string | undefined;
  prospectId: number | undefined;
  currentStatus?: ProspectStatusType;
  onSuccess: (newStatus: ProspectStatusType) => void;
  onError?: () => void;
}

export const useProspectStatusUpdate = ({
  brokerId,
  prospectId,
  currentStatus,
  onSuccess,
  onError
}: IUseProspectStatusUpdateProps) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const handleMarkAsLost = useCallback(async () => {
    if (!brokerId || prospectId == null) return;
    if (currentStatus === ProspectStatus.Lost) return;
    try {
      setIsUpdatingStatus(true);
      await ProspectsService.updateProspect(brokerId, prospectId, { status: ProspectStatus.Lost });
      onSuccess(ProspectStatus.Lost);
    } catch {
      onError?.();
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [brokerId, prospectId, currentStatus, onSuccess, onError]);

  const handleMarkAsInactive = useCallback(async () => {
    if (!brokerId || prospectId == null) return;
    if (currentStatus === ProspectStatus.Inactive) return;
    try {
      setIsUpdatingStatus(true);
      await ProspectsService.updateProspect(brokerId, prospectId, { status: ProspectStatus.Inactive });
      onSuccess(ProspectStatus.Inactive);
    } catch {
      onError?.();
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [brokerId, prospectId, currentStatus, onSuccess, onError]);

  return { handleMarkAsLost, handleMarkAsInactive, isUpdatingStatus };
};
